import type { DriveStep } from 'driver.js';

/**
 * Guided tour for the redesigned lead workspace (OPS shell).
 * Order mirrors how staff read the page: project sidebar → client header →
 * stage rail + advance/retreat → step detail → documents → team notes →
 * activity rail → help button.
 */
export const CLIENT_WORKSPACE_TOUR_SELECTORS = {
  sidebar: '[data-tour="cw-sidebar"]',
  sidebarProjects: '[data-tour="cw-sidebar-projects"]',
  sidebarPortal: '[data-tour="cw-sidebar-portal"]',
  sidebarNav: '[data-tour="cw-sidebar-nav"]',
  header: '[data-tour="cw-header"]',
  mapChip: '[data-tour="cw-map-chip"]',
  attribution: '[data-tour="cw-attribution"]',
  stageRail: '[data-tour="cw-stage-rail"]',
  stageFooter: '[data-tour="cw-stage-footer"]',
  nextActions: '[data-tour="cw-next-actions"]',
  stepDetail: '[data-tour="cw-step-detail"]',
  documents: '[data-tour="cw-documents"]',
  teamNotes: '[data-tour="cw-team-notes"]',
  rightRail: '[data-tour="cw-right-rail"]',
  helpBtn: '[data-tour="cw-help-btn"]',
} as const;

/** Window event the comments strip listens for so the tour can expand it on its step. */
const EXPAND_AGENT_COMMENTS_EVENT = 'lctf:expandAgentComments';

export function buildClientWorkspaceTourSteps(): DriveStep[] {
  const steps: DriveStep[] = [
    {
      popover: {
        title: 'The new Lead Workspace',
        description:
          'This page was rebuilt around the OPS layout: projects on the left, one compact client header, a tight stage rail, and live activity on the right. Here is a quick walk-through.',
      },
    },
  ];

  const optional: { selector: string; step: DriveStep }[] = [
    {
      selector: CLIENT_WORKSPACE_TOUR_SELECTORS.sidebarProjects,
      step: {
        element: CLIENT_WORKSPACE_TOUR_SELECTORS.sidebarProjects,
        popover: {
          title: 'Projects sidebar',
          description:
            'Every project for this client, always visible — no more horizontal scrolling. Each row shows the project number and a live stage chip; the highlighted row is the project you are working on. Use the ⋯ menu to rename, transfer, hide, or delete.',
          side: 'right',
          align: 'start',
        },
      },
    },
    {
      selector: CLIENT_WORKSPACE_TOUR_SELECTORS.sidebarPortal,
      step: {
        element: CLIENT_WORKSPACE_TOUR_SELECTORS.sidebarPortal,
        popover: {
          title: 'Portal link & PIN',
          description:
            'The client portal link, PIN, copy, and preview all live here. Create the portal from this block if one does not exist yet.',
          side: 'right',
          align: 'start',
        },
      },
    },
    {
      selector: CLIENT_WORKSPACE_TOUR_SELECTORS.header,
      step: {
        element: CLIENT_WORKSPACE_TOUR_SELECTORS.header,
        popover: {
          title: 'Client header',
          description:
            'Client details moved out of their own card and into this header — name, status, and contact chips. The pin button pops out a greyscale map of their location, and the attribution badge shows where the lead came from (click it for UTM detail).',
          side: 'bottom',
          align: 'start',
        },
      },
    },
    {
      selector: CLIENT_WORKSPACE_TOUR_SELECTORS.stageRail,
      step: {
        element: CLIENT_WORKSPACE_TOUR_SELECTORS.stageRail,
        popover: {
          title: 'Stage rail',
          description:
            'The active project name is always shown in the rail header so you never lose context. Solid = completed, ringed = the current stage, muted = locked, struck = skipped. Click any step to view its detail without changing the workflow.',
          side: 'bottom',
          align: 'start',
        },
      },
    },
    {
      selector: CLIENT_WORKSPACE_TOUR_SELECTORS.stageFooter,
      step: {
        element: CLIENT_WORKSPACE_TOUR_SELECTORS.stageFooter,
        popover: {
          title: 'Stage controls & suggestions',
          description:
            'Every stage moves through this one module — no more scattered buttons. Suggested follow-ups (nudge an idle questionnaire, follow up on an opened proposal) sit on top with one-click actions. Retreating pins the stage where you put it until you advance again, so the timeline always shows the truth.',
          side: 'bottom',
          align: 'start',
        },
      },
    },
    {
      selector: CLIENT_WORKSPACE_TOUR_SELECTORS.stepDetail,
      step: {
        element: CLIENT_WORKSPACE_TOUR_SELECTORS.stepDetail,
        popover: {
          title: 'Step detail',
          description:
            'The full workspace for the selected stage — agreements, models, proposals, plans, production, delivery. Cards share one layout now, so the panel keeps its shape as you click through stages.',
          side: 'right',
          align: 'start',
        },
      },
    },
    {
      selector: CLIENT_WORKSPACE_TOUR_SELECTORS.documents,
      step: {
        element: CLIENT_WORKSPACE_TOUR_SELECTORS.documents,
        popover: {
          title: 'Documents & plans',
          description:
            'Upload drawings here and filter by project. The bidding agent writes a synopsis on each file and drops markup pins where it extracts data — click a pin to open the viewer at that spot.',
          side: 'top',
          align: 'start',
        },
      },
    },
    {
      selector: CLIENT_WORKSPACE_TOUR_SELECTORS.teamNotes,
      step: {
        element: CLIENT_WORKSPACE_TOUR_SELECTORS.teamNotes,
        popover: {
          title: 'Team notes',
          description:
            'Internal-only coordination for this lead, collapsed by default. Document-specific discussion lives on the document cards, not here.',
          side: 'top',
          align: 'start',
          onPopoverRender: () => {
            try {
              window.dispatchEvent(new Event(EXPAND_AGENT_COMMENTS_EVENT));
            } catch {
              /* ignore */
            }
          },
        },
      },
    },
    {
      selector: CLIENT_WORKSPACE_TOUR_SELECTORS.rightRail,
      step: {
        element: CLIENT_WORKSPACE_TOUR_SELECTORS.rightRail,
        popover: {
          title: 'Live activity',
          description:
            'Everything the client (and agents) do, in real time — portal visits, proposal views, signatures, payments. Agent events get their own tag and filter tab.',
          side: 'left',
          align: 'start',
        },
      },
    },
    {
      selector: CLIENT_WORKSPACE_TOUR_SELECTORS.helpBtn,
      step: {
        element: CLIENT_WORKSPACE_TOUR_SELECTORS.helpBtn,
        popover: {
          title: 'Need help?',
          description: 'Open the help guide or replay this tour from the header anytime.',
          side: 'bottom',
          align: 'end',
        },
      },
    },
  ];

  for (const item of optional) {
    if (typeof document !== 'undefined' && document.querySelector(item.selector)) {
      steps.push(item.step);
    }
  }

  return steps;
}

// v3: bumped for the OPS-shell workspace redesign so every employee is
// auto-prompted to walk through the new layout on first visit.
export const CLIENT_WORKSPACE_TOUR_STORAGE_KEY = 'lctf:clientWorkspaceTour:v3';

export function markClientWorkspaceTourSeen(): void {
  try {
    localStorage.setItem(CLIENT_WORKSPACE_TOUR_STORAGE_KEY, new Date().toISOString());
  } catch {
    /* ignore */
  }
}

export function hasSeenClientWorkspaceTour(): boolean {
  try {
    return !!localStorage.getItem(CLIENT_WORKSPACE_TOUR_STORAGE_KEY);
  } catch {
    return false;
  }
}
